import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import reporteService from '../../services/reporteService';
import { formatearMoneda, formatearFecha } from '../../utils/helpers';
import {
  FaChartBar, FaFilePdf, FaFileExcel, FaPlay,
  FaUsers, FaTags, FaCheckCircle, FaDownload, FaTrash
} from 'react-icons/fa';
import api from '../../services/api';

export default function ReportesPage() {
  const [reportes,    setReportes]    = useState([]);
  const [cargando,    setCargando]    = useState(false);
  const [generando,   setGenerando]   = useState(false);
  const [resultado,   setResultado]   = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);
  const [eliminando,  setEliminando]  = useState(false);

  const [form, setForm] = useState({
    tipo:           'balance',
    periodo_inicio: '',
    periodo_fin:    '',
    formato:        'pdf',
  });

  useEffect(() => { cargarReportes(); }, []);

  const cargarReportes = async () => {
    setCargando(true);
    try {
      const data = await reporteService.listar();
      setReportes(data || []);
      setSeleccionados([]);
    } finally {
      setCargando(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerar = async (e) => {
    e.preventDefault();
    setGenerando(true);
    setResultado(null);
    try {
      const data = await reporteService.generar(
        form.tipo, form.periodo_inicio,
        form.periodo_fin, form.formato
      );
      setResultado(data);
      cargarReportes();
    } catch (err) {
      console.error('Error al generar reporte:', err);
    } finally {
      setGenerando(false);
    }
  };

  const descargarReporte = async (id, nombre) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://sistemafinanciero.digitaleseduproperu.com/api/reportes/${id}/descargar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al descargar');
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = nombre || `reporte_${id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar:', err);
    }
  };

  const toggleSeleccion = (id) => {
    setSeleccionados(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleTodos = () => {
    if (seleccionados.length === reportes.length) {
      setSeleccionados([]);
    } else {
      setSeleccionados(reportes.map(r => r.id));
    }
  };

  const eliminarSeleccionados = async () => {
    if (seleccionados.length === 0) return;
    if (!window.confirm(`¿Eliminar ${seleccionados.length} reporte(s)?`)) return;
    setEliminando(true);
    try {
      await api.post('/reportes/eliminar-multiple', { ids: seleccionados });
      cargarReportes();
    } catch (err) {
      console.error('Error al eliminar:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <h4>Reportes</h4>
        <p>Generación de reportes financieros en PDF y Excel</p>
      </div>

      <div className="row g-3">

        <div className="col-md-4">
          <div className="card card-custom">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <FaChartBar className="me-2 text-primary-custom" />
                Generar Reporte
              </h6>
              <form onSubmit={handleGenerar}>
                <div className="mb-3">
                  <label className="form-label">Tipo de Reporte *</label>
                  <select name="tipo" className="form-select"
                    value={form.tipo} onChange={handleChange} required>
                    <option value="balance">Balance General</option>
                    <option value="ingresos">Ingresos</option>
                    <option value="gastos">Gastos</option>
                    <option value="comprobantes">Comprobantes</option>
                    <option value="consolidado">Consolidado Admin y Contador</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Fecha Inicio *</label>
                  <input name="periodo_inicio" type="date"
                    className="form-control" value={form.periodo_inicio}
                    onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Fecha Fin *</label>
                  <input name="periodo_fin" type="date"
                    className="form-control" value={form.periodo_fin}
                    onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Formato *</label>
                  <div className="d-flex gap-2">
                    <button type="button"
                      className={`btn flex-fill ${form.formato === 'pdf' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setForm(p => ({ ...p, formato: 'pdf' }))}>
                      <FaFilePdf className="me-1" /> PDF
                    </button>
                    <button type="button"
                      className={`btn flex-fill ${form.formato === 'excel' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setForm(p => ({ ...p, formato: 'excel' }))}>
                      <FaFileExcel className="me-1" /> Excel
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-danger w-100" disabled={generando}>
                  <FaPlay className="me-2" />
                  {generando ? 'Generando...' : 'Generar Reporte'}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-8">

          {resultado && (
            <div className="card card-custom mb-3">
              <div className="card-body">
                <h6 className="fw-bold mb-3 text-success">
                  <FaCheckCircle className="me-2" />
                  Reporte generado correctamente
                </h6>
                {resultado.reporte?.tipo === 'balance' && resultado.datos && (
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="p-3 bg-success bg-opacity-10 rounded text-center">
                        <div className="fw-bold text-success fs-5">{formatearMoneda(resultado.datos.ingresos)}</div>
                        <small className="text-muted">Total Ingresos</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-danger bg-opacity-10 rounded text-center">
                        <div className="fw-bold text-danger fs-5">{formatearMoneda(resultado.datos.gastos)}</div>
                        <small className="text-muted">Total Gastos</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-dark bg-opacity-10 rounded text-center">
                        <div className="fw-bold fs-5">{formatearMoneda(resultado.datos.ingresos - resultado.datos.gastos)}</div>
                        <small className="text-muted">Saldo Neto</small>
                      </div>
                    </div>
                  </div>
                )}
                {resultado.reporte?.tipo !== 'balance' &&
                 resultado.reporte?.tipo !== 'consolidado' &&
                 Array.isArray(resultado.datos) && (
                  <p className="text-muted mb-0">
                    Se encontraron <strong>{resultado.datos.length}</strong> registros en el período seleccionado.
                  </p>
                )}
                {resultado.reporte?.tipo === 'consolidado' && resultado.datos?.resumen && (
                  <>
                    <div className="row g-2 mb-4">
                      {[
                        { label: 'Total ingresos', value: formatearMoneda(resultado.datos.resumen.total_ingresos), color: 'success' },
                        { label: 'Total gastos',   value: formatearMoneda(resultado.datos.resumen.total_gastos),   color: 'danger'  },
                        { label: 'Saldo neto',     value: formatearMoneda(resultado.datos.resumen.saldo_neto),     color: 'dark'    },
                        { label: 'Comprobantes',   value: resultado.datos.resumen.total_comprobantes,              color: 'info'    },
                        { label: 'Pendientes',     value: resultado.datos.resumen.comprobantes_pendientes,         color: 'warning' },
                      ].map((k, i) => (
                        <div className="col" key={i}>
                          <div className={`p-2 bg-${k.color} bg-opacity-10 rounded text-center`}>
                            <div className={`fw-bold text-${k.color}`}>{k.value}</div>
                            <small className="text-muted" style={{ fontSize: 11 }}>{k.label}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                    {resultado.datos.por_usuario?.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-bold mb-2">
                          <FaUsers className="me-2 text-primary-custom" />Por usuario
                        </h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-custom mb-0" style={{ fontSize: 13 }}>
                            <thead>
                              <tr>
                                <th>Usuario</th><th>Rol</th><th>Ingresos</th>
                                <th>Gastos</th><th>Saldo neto</th><th>Comprobantes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {resultado.datos.por_usuario.map((u, i) => (
                                <tr key={i}>
                                  <td className="fw-bold">{u.usuario}</td>
                                  <td><span className={`badge ${u.rol === 'administrador' ? 'bg-danger' : 'bg-warning text-dark'}`}>{u.rol}</span></td>
                                  <td className="text-success fw-bold">{formatearMoneda(u.ingresos)}</td>
                                  <td className="text-danger fw-bold">{formatearMoneda(u.gastos)}</td>
                                  <td className={`fw-bold ${u.saldo_neto >= 0 ? 'text-primary' : 'text-danger'}`}>{formatearMoneda(u.saldo_neto)}</td>
                                  <td className="text-center">{u.comprobantes}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {resultado.datos.por_categoria?.length > 0 && (
                      <div>
                        <h6 className="fw-bold mb-2">
                          <FaTags className="me-2 text-primary-custom" />Gastos por categoría
                        </h6>
                        <div className="row g-2">
                          {resultado.datos.por_categoria.map((c, i) => (
                            <div className="col-md-4" key={i}>
                              <div className="p-2 rounded" style={{ background: '#f9f9f9', border: '1px solid #eee' }}>
                                <div className="small fw-bold text-truncate">{c.categoria}</div>
                                <div className="text-danger fw-bold">{formatearMoneda(c.total)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="card card-custom">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">Historial de Reportes</h6>
                {seleccionados.length > 0 && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={eliminarSeleccionados}
                    disabled={eliminando}
                  >
                    <FaTrash className="me-1" />
                    {eliminando ? 'Eliminando...' : `Eliminar ${seleccionados.length} seleccionado(s)`}
                  </button>
                )}
              </div>
              {cargando ? (
                <div className="text-center py-3">
                  <div className="spinner-custom mx-auto" />
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-custom table-sm">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={seleccionados.length === reportes.length && reportes.length > 0}
                            onChange={toggleTodos}
                          />
                        </th>
                        <th>Tipo</th>
                        <th>Período</th>
                        <th>Formato</th>
                        <th>Estado</th>
                        <th>Fecha</th>
                        <th>Descargar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportes.length > 0 ? (
                        reportes.map((r) => (
                          <tr key={r.id} className={seleccionados.includes(r.id) ? 'table-warning' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={seleccionados.includes(r.id)}
                                onChange={() => toggleSeleccion(r.id)}
                              />
                            </td>
                            <td>
                              <span className={`badge ${r.tipo === 'consolidado' ? 'bg-primary' : 'bg-secondary'}`}>
                                {r.tipo}
                              </span>
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {formatearFecha(r.periodo_inicio)} — {formatearFecha(r.periodo_fin)}
                            </td>
                            <td>
                              {r.formato === 'pdf'
                                ? <span className="badge bg-danger">PDF</span>
                                : <span className="badge bg-success">Excel</span>
                              }
                            </td>
                            <td><span className="badge badge-activo">{r.estado}</span></td>
                            <td style={{ fontSize: 12 }}>{formatearFecha(r.generado_at)}</td>
                            <td>
                              {r.archivo_url
                                ? (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    style={{ fontSize: 11 }}
                                    onClick={() => descargarReporte(r.id, r.archivo_url)}
                                  >
                                    <FaDownload className="me-1" />
                                    {r.formato === 'excel' ? 'CSV' : 'PDF'}
                                  </button>
                                )
                                : <span className="text-muted" style={{ fontSize: 11 }}>—</span>
                              }
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-3">
                            Sin reportes generados
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
}